import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  used: "bg-slate-100 text-slate-700 border-slate-200",
  disabled: "bg-rose-100 text-rose-700 border-rose-200",
  expired: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function AdminBetaCodes() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignedEmail, setAssignedEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [initialGrant, setInitialGrant] = useState("");
  const [busy, setBusy] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(assignedEmail.trim());
  const grantInt = parseInt(initialGrant, 10);
  const grantValid = !Number.isNaN(grantInt) && grantInt >= 1 && grantInt <= 10000;
  const canGenerate = emailValid && grantValid && !busy;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/beta-codes");
      setRows(data.codes);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createCode = async (e) => {
    e.preventDefault();
    if (!canGenerate) return;
    setBusy(true);
    try {
      const payload = {
        assigned_email: assignedEmail.trim(),
        initial_credit_grant: grantInt,
      };
      if (expiresAt) {
        // datetime-local produces "YYYY-MM-DDTHH:mm" — send as ISO UTC.
        payload.expires_at = new Date(expiresAt).toISOString();
      }
      await api.post("/admin/beta-codes", payload);
      toast.success("Code generated");
      setAssignedEmail("");
      setExpiresAt("");
      setInitialGrant("");
      await refresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setBusy(false);
    }
  };

  const patchCode = async (code_id, body, msg) => {
    try {
      await api.patch(`/admin/beta-codes/${code_id}`, body);
      toast.success(msg);
      await refresh();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  const setNewExpiry = (code_id) => {
    const raw = window.prompt(
      "Enter new expiry (YYYY-MM-DDTHH:mm in your local time, or blank to clear):",
      "",
    );
    if (raw === null) return;
    if (!raw) patchCode(code_id, { clear_expiry: true }, "Expiry cleared");
    else patchCode(code_id, { expires_at: new Date(raw).toISOString() }, "Expiry updated");
  };

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-beta-codes">
      <AdminNav title="Beta Access Codes" />

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        <form
          onSubmit={createCode}
          className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3"
          data-testid="admin-create-form"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">
            Generate new code
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">
                Assigned email <span className="text-rose-600">*</span>
              </span>
              <input
                type="email"
                required
                value={assignedEmail}
                onChange={(e) => setAssignedEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
                data-testid="admin-assigned-email"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">
                Expires at (optional, local time)
              </span>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
                data-testid="admin-expires-at"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">
                Initial credit grant <span className="text-rose-600">*</span>
              </span>
              <input
                type="number"
                required
                min="1"
                max="10000"
                value={initialGrant}
                onChange={(e) => setInitialGrant(e.target.value)}
                placeholder="e.g. 200"
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
                data-testid="admin-initial-grant"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={!canGenerate}
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-generate-btn"
          >
            {busy ? "Generating…" : "Generate code"}
          </button>
        </form>

        <div className="rounded-2xl border border-[color:var(--border-stone)] bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-stone)]">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">
              All codes ({rows.length})
            </div>
            <button
              type="button"
              onClick={refresh}
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs hover:bg-[color:var(--bg-surface-hover)]"
              data-testid="admin-refresh"
            >
              Refresh
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]">No codes yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-codes-table">
                <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Assigned</th>
                    <th className="text-right px-3 py-2">Grant</th>
                    <th className="text-left px-3 py-2">Created</th>
                    <th className="text-left px-3 py-2">Expires</th>
                    <th className="text-left px-3 py-2">Redeemed</th>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr
                      key={c.code_id}
                      className="border-t border-[color:var(--border-stone)] align-top"
                      data-testid={`admin-code-row-${c.code_id}`}
                    >
                      <td className="px-3 py-2 font-mono">{c.code}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                            STATUS_COLORS[c.status] || "bg-slate-100"
                          }`}
                          data-testid={`admin-code-status-${c.code_id}`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 break-all">{c.assigned_email || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.initial_credit_grant || 0}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {fmt(c.created_at)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {fmt(c.expires_at) || "—"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {fmt(c.used_at) || "—"}
                      </td>
                      <td className="px-3 py-2 font-mono break-all">
                        {c.used_by_user_id || "—"}
                      </td>
                      <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                        {c.status !== "used" && !c.disabled && (
                          <button
                            type="button"
                            onClick={() => patchCode(c.code_id, { disabled: true }, "Disabled")}
                            className="text-rose-600 underline"
                            data-testid={`admin-disable-${c.code_id}`}
                          >
                            Disable
                          </button>
                        )}
                        {c.disabled && !c.used_at && (
                          <button
                            type="button"
                            onClick={() => patchCode(c.code_id, { disabled: false }, "Re-enabled")}
                            className="text-emerald-700 underline"
                            data-testid={`admin-enable-${c.code_id}`}
                          >
                            Re-enable
                          </button>
                        )}
                        {!c.used_at && (
                          <button
                            type="button"
                            onClick={() => setNewExpiry(c.code_id)}
                            className="text-[color:var(--brand)] underline"
                            data-testid={`admin-expiry-${c.code_id}`}
                          >
                            Set expiry
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="text-[11px] text-[color:var(--text-secondary)]">
          Signed in as <span className="font-mono">{user?.email}</span>
        </div>
      </main>
    </div>
  );
}

function fmt(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}
